import React from 'react';
import { Form, Tabs } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { useActiveSection } from '../../../../queries/guides';
import { useSectionMutations } from '../../../../mutations/sections';
import {
  SECTION_PADDING_LARGE,
  SECTION_PADDING_MEDIUM,
  SECTION_PADDING_SMALL,
} from '../../../../constants/sections';
import { StyledInput } from '../../Styles';
import {
  IconPaddingLeftAndRight,
  IconPaddingTopAndBottom,
} from '@bynder/icons';

export const SectionPaddingEditor = () => {
  const { activeSection } = useActiveSection();
  const { translate } = useTranslate();
  const { editSectionHorizontalPadding, editSectionVerticalPadding } =
    useSectionMutations();

  const [verticalPadding, setVerticalPadding] = React.useState(
    activeSection?.appearance.paddingTop || 0,
  );
  const [horizontalPadding, setHorizontalPadding] = React.useState(
    activeSection?.appearance.paddingLeft || 0,
  );

  React.useEffect(() => {
    setVerticalPadding(activeSection.appearance.paddingTop);
    setHorizontalPadding(activeSection.appearance.paddingLeft);
  }, [
    activeSection.appearance.paddingLeft,
    activeSection.appearance.paddingTop,
    activeSection.id,
  ]);

  const handleHorizontalBlur = (
    event: React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    const newHorizontalPadding = setPaddingLimits(Number(event.target.value));

    setHorizontalPadding(newHorizontalPadding);

    if (newHorizontalPadding !== activeSection.appearance.paddingLeft) {
      editSectionHorizontalPadding.mutate({
        sectionId: activeSection.id,
        horizontalPadding: newHorizontalPadding,
      });
    }
  };

  const handleClickHorizontalPaddingPreset = (value: number) => {
    if (value !== activeSection.appearance.paddingLeft) {
      editSectionHorizontalPadding.mutate({
        sectionId: activeSection.id,
        horizontalPadding: value,
      });
    }
  };

  const handleClickVerticalPaddingPreset = (value: number) => {
    if (value !== activeSection.appearance.paddingTop) {
      editSectionVerticalPadding.mutate({
        sectionId: activeSection.id,
        verticalPadding: value,
      });
    }
  };

  const handleVerticalBlur = (
    event: React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    const newVerticalPadding = setPaddingLimits(Number(event.target.value));
    setVerticalPadding(newVerticalPadding);

    if (newVerticalPadding !== activeSection.appearance.paddingTop) {
      editSectionVerticalPadding.mutate({
        sectionId: activeSection.id,
        verticalPadding: newVerticalPadding,
      });
    }
  };

  const handleHorizontalEnter = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      if (horizontalPadding !== activeSection.appearance.paddingLeft) {
        editSectionHorizontalPadding.mutate({
          sectionId: activeSection.id,
          horizontalPadding,
        });
      }
    }
  };
  const handleVerticalEnter = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      if (horizontalPadding !== activeSection.appearance.paddingTop) {
        editSectionVerticalPadding.mutate({
          sectionId: activeSection.id,
          verticalPadding,
        });
      }
    }
  };

  return (
    <>
      <Form.Row>
        <Form.Group>
          <StyledInput
            value={horizontalPadding.toString()}
            onChange={value => {
              if (value === '') setHorizontalPadding(0);
              else setHorizontalPadding(Number(value));
            }}
            onBlur={handleHorizontalBlur}
            onKeyDown={handleHorizontalEnter}
            type="number"
            suffix="px"
            icon={<IconPaddingLeftAndRight />}
            id="horizontal_padding"
          />
        </Form.Group>
        <Form.Group>
          <Tabs variant="clean">
            <Tabs.Tab
              data-testid="horizontal-padding-small"
              label={translate('SECTION_INSPECTOR.SECTION_PADDING_SMALL')}
              isActive={activeSection.appearance.paddingLeft === SECTION_PADDING_SMALL}
              onClick={() => handleClickHorizontalPaddingPreset(SECTION_PADDING_SMALL)}
            />
            <Tabs.Tab
              data-testid="horizontal-padding-medium"
              label={translate('SECTION_INSPECTOR.SECTION_PADDING_MEDIUM')}
              isActive={activeSection.appearance.paddingLeft === SECTION_PADDING_MEDIUM}
              onClick={() => handleClickHorizontalPaddingPreset(SECTION_PADDING_MEDIUM)}
            />
            <Tabs.Tab
              data-testid="horizontal-padding-large"
              label={translate('SECTION_INSPECTOR.SECTION_PADDING_LARGE')}
              isActive={activeSection.appearance.paddingLeft === SECTION_PADDING_LARGE}
              onClick={() => handleClickHorizontalPaddingPreset(SECTION_PADDING_LARGE)}
            />
          </Tabs>
        </Form.Group>
      </Form.Row>
      <Form.Row>
        <Form.Group>
          <StyledInput
            data-testid="vertical-padding-input"
            type="number"
            suffix="px"
            icon={<IconPaddingTopAndBottom />}
            value={verticalPadding?.toString()}
            onChange={newValue => {
              if (newValue === '') setVerticalPadding(0);
              else setVerticalPadding(Number(newValue));
            }}
            onKeyDown={handleVerticalEnter}
            onBlur={handleVerticalBlur}
          />
        </Form.Group>
        <Form.Group>
          <Tabs variant="clean">
            <Tabs.Tab
              data-testid="vertical-padding-small"
              label={translate('SECTION_INSPECTOR.SECTION_PADDING_SMALL')}
              isActive={verticalPadding === SECTION_PADDING_SMALL}
              onClick={() => handleClickVerticalPaddingPreset(SECTION_PADDING_SMALL)}
            />
            <Tabs.Tab
              data-testid="vertical-padding-medium"
              label={translate('SECTION_INSPECTOR.SECTION_PADDING_MEDIUM')}
              isActive={verticalPadding === SECTION_PADDING_MEDIUM}
              onClick={() => handleClickVerticalPaddingPreset(SECTION_PADDING_MEDIUM)}
            />
            <Tabs.Tab
              data-testid="vertical-padding-large"
              label={translate('SECTION_INSPECTOR.SECTION_PADDING_LARGE')}
              isActive={verticalPadding === SECTION_PADDING_LARGE}
              onClick={() => handleClickVerticalPaddingPreset(SECTION_PADDING_LARGE)}
            />
          </Tabs>
        </Form.Group>
      </Form.Row>
    </>
  );
};

const setPaddingLimits = (value: number) => {
  if (value > 100) return 100;
  else if (value < 0) return 0;

  return value;
};
